import { useRef, type KeyboardEvent, type ReactNode, type RefObject } from 'react';
import { MagnifyingGlassIcon } from '@phosphor-icons/react';
import { wantedAttr } from '../field_trigger.js';
import { PageFieldSheet } from '../page_field_sheet.js';
import { returnFocusUnlessMoved } from '../return_focus.js';
import {
  PlaceSearchInput,
  TriggerContent,
  TriggerShell,
  type PlaceFieldValue,
  type PlaceFieldVariant,
} from './place_field_trigger.js';

/**
 * A FOLHA de tela cheia do campo (#2653) — a receita do cartão do `Onde` da
 * folha do hotel, na casca do Drawer do DS: cabeçalho com seta de voltar e a
 * pergunta do campo, o input da busca numa caixa em cima, a lista embaixo
 * tomando a altura que houver. Fechado, o gatilho é a pílula de sempre (aqui
 * ela nunca hospeda o input — a busca é na folha), então volta a ser um botão
 * de verdade pro teclado. A folha devolve o foco a ele ao fechar — a menos
 * que o foco já tenha ido pra outro lugar.
 *
 * O cursor da busca (#4185): o `autoFocus` foca o input DENTRO do gesto (é o que
 * sobe o teclado no iOS), e a casca o reafirma ao fim da entrada
 * (`initialFocus` da `BottomSheet`) — a folha anterior do preenchimento guiado
 * devolve o foco ao gatilho dela depois que esta já montou.
 */
export function PlaceSheet({
  open,
  disabled,
  label,
  subtitle,
  backLabel,
  variant,
  value,
  emptyText,
  icon,
  term,
  onTerm,
  panelRef,
  wanted,
  onOpen,
  onClose,
  children,
}: {
  readonly open: boolean;
  readonly disabled: boolean;
  readonly label: string;
  /** Ver `PageFieldSheet.subtitle`. */
  readonly subtitle?: string;
  readonly backLabel: string;
  readonly variant: PlaceFieldVariant;
  readonly value: PlaceFieldValue | undefined;
  readonly emptyText: string;
  readonly icon: ReactNode | undefined;
  readonly term: string;
  readonly onTerm: (term: string) => void;
  readonly panelRef: RefObject<HTMLDivElement | null>;
  readonly wanted: boolean;
  readonly onOpen: () => void;
  readonly onClose: () => void;
  readonly children: ReactNode;
}): ReactNode {
  /* O gatilho é DESTA casca: é a ele que o Drawer devolve o foco ao fechar. */
  const triggerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const openIfEnabled = () => {
    if (!disabled) onOpen();
  };
  const trigger = (
    <TriggerContent
      open={false}
      {...(value ? { value } : {})}
      emptyText={emptyText}
      icon={icon}
      label={label}
      term={term}
      onTerm={onTerm}
      panelRef={panelRef}
      onEscape={onClose}
      focusOnOpen={false}
    />
  );
  const search = (
    <PlaceSearchInput
      term={term}
      onTerm={onTerm}
      panelRef={panelRef}
      label={label}
      onEscape={onClose}
      autoFocus
      inputRef={searchRef}
      placeholder={value ? [value.code, value.title].filter(Boolean).join(' ') : emptyText}
    />
  );
  return (
    <>
      <TriggerShell
        ref={triggerRef}
        open={false}
        disabled={disabled}
        label={label}
        variant={variant}
        {...wantedAttr(wanted)}
        onClick={openIfEnabled}
        onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          openIfEnabled();
        }}
      >
        {trigger}
      </TriggerShell>
      <PageFieldSheet
        open={open}
        onClose={onClose}
        title={label}
        {...(subtitle ? { subtitle } : {})}
        backLabel={backLabel}
        /* Ao gatilho — salvo quando o foco já pousou noutro lugar: o
           preenchimento guiado abre a folha do campo seguinte enquanto esta
           ainda sai, e o foco é dela (ver `returnFocusUnlessMoved`). */
        returnFocusTo={() => returnFocusUnlessMoved(triggerRef)()}
        initialFocus={searchRef}
      >
        <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto scrollbar-rail p-4 pr-[calc(1rem-var(--scrollbar-rail))]">
          <div className="flex h-11 shrink-0 items-center gap-2 rounded-xl border border-input px-3 transition-colors focus-within:border-ring focus-within:ring-3 focus-within:ring-ring/50">
            <MagnifyingGlassIcon
              className="size-4 shrink-0 text-muted-foreground"
              aria-hidden="true"
            />
            {search}
          </div>
          <div ref={panelRef} className="contents">
            {children}
          </div>
        </div>
      </PageFieldSheet>
    </>
  );
}
